import React, { useState, useMemo } from 'react';
import { DataManager } from '../../services/dataManager';

const SupervisionAccessLogModal = ({
    clients = [],
    counselorsList = [],
    user,
    isOpen,
    onClose,
    onOpenClient,
    onRefresh
}) => {
    const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'approved' | 'pending' | 'rejected' | 'revoked'
    const [searchTerm, setSearchTerm] = useState('');
    const [actionLoadingId, setActionLoadingId] = useState(null);

    // Aggregate all access logs and active client grants
    const { allLogs, activeGrants, pendingRequests, rejectedRequests } = useMemo(() => {
        const logs = [];
        const grants = [];
        const pendings = [];
        const rejecteds = [];

        (clients || []).forEach(c => {
            const counselorObj = counselorsList.find(cn => cn.id === c.counselorId || cn.email === c.counselorEmail);
            const counselorDisplayName = counselorObj?.name || c.counselorName || c.counselorEmail || 'Counselor Titolare';

            if (c.superAdminAccessStatus === 'approved') {
                grants.push(c);
            } else if (c.superAdminAccessStatus === 'pending') {
                pendings.push(c);
            } else if (c.superAdminAccessStatus === 'rejected') {
                rejecteds.push(c);
            }

            // Extract explicit log history if present
            if (Array.isArray(c.superAdminAccessLog) && c.superAdminAccessLog.length > 0) {
                c.superAdminAccessLog.forEach(entry => {
                    logs.push({
                        ...entry,
                        clientId: c.id,
                        clientName: c.name || 'Cliente senza nome',
                        clientEmail: c.email || '',
                        counselorId: c.counselorId,
                        counselorEmail: c.counselorEmail,
                        counselorName: counselorDisplayName,
                        currentStatus: c.superAdminAccessStatus || 'none',
                        rawClient: c
                    });
                });
            } else if (c.superAdminAccessStatus && c.superAdminAccessStatus !== 'none') {
                // Synthesize from current state for legacy/migrated records
                logs.push({
                    id: `legacy_${c.id}`,
                    action: c.superAdminAccessStatus === 'approved' ? 'approved' : c.superAdminAccessStatus === 'pending' ? 'requested' : 'rejected',
                    timestamp: c.superAdminAccessApprovedAt || c.superAdminAccessRequestedAt || c.updatedAt || new Date().toISOString(),
                    userEmail: c.superAdminAccessApprovedBy || c.superAdminAccessRequestedBy || 'SuperAdmin',
                    userName: c.superAdminAccessApprovedByName || c.superAdminAccessRequestedByName || 'SuperAdmin',
                    notes: c.superAdminAccessNotes || '',
                    clientId: c.id,
                    clientName: c.name || 'Cliente senza nome',
                    clientEmail: c.email || '',
                    counselorId: c.counselorId,
                    counselorEmail: c.counselorEmail,
                    counselorName: counselorDisplayName,
                    currentStatus: c.superAdminAccessStatus,
                    rawClient: c
                });
            }
        });

        // Sort logs newest first
        logs.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

        return {
            allLogs: logs,
            activeGrants: grants,
            pendingRequests: pendings,
            rejectedRequests: rejecteds
        };
    }, [clients, counselorsList]);

    // Filtered logs
    const filteredLogs = useMemo(() => {
        return allLogs.filter(log => {
            const matchesStatus =
                filterStatus === 'all' ||
                (filterStatus === 'approved' && log.action === 'approved') ||
                (filterStatus === 'pending' && (log.action === 'requested' || log.action === 'pending')) ||
                (filterStatus === 'rejected' && log.action === 'rejected') ||
                (filterStatus === 'revoked' && (log.action === 'revoked' || log.action === 'cancelled'));

            const q = searchTerm.toLowerCase().trim();
            const matchesSearch =
                !q ||
                log.clientName.toLowerCase().includes(q) ||
                log.clientEmail.toLowerCase().includes(q) ||
                log.counselorName.toLowerCase().includes(q) ||
                (log.userName && log.userName.toLowerCase().includes(q)) ||
                (log.notes && log.notes.toLowerCase().includes(q));

            return matchesStatus && matchesSearch;
        });
    }, [allLogs, filterStatus, searchTerm]);

    const formatLogDate = (dateVal) => {
        if (!dateVal) return 'N/D';
        try {
            return new Date(dateVal).toLocaleString('it-IT', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateVal;
        }
    };

    const getActionBadge = (action) => {
        switch (action) {
            case 'approved':
                return {
                    label: 'Grant Concesso',
                    icon: '🛡️',
                    bg: 'bg-emerald-100 text-emerald-800 border-emerald-200'
                };
            case 'requested':
            case 'pending':
                return {
                    label: 'Richiesta Inviata',
                    icon: '⏳',
                    bg: 'bg-amber-100 text-amber-800 border-amber-200'
                };
            case 'rejected':
                return {
                    label: 'Richiesta Respinta',
                    icon: '🚫',
                    bg: 'bg-rose-100 text-rose-800 border-rose-200'
                };
            case 'revoked':
                return {
                    label: 'Accesso Revocato',
                    icon: '🔒',
                    bg: 'bg-slate-100 text-slate-700 border-slate-200'
                };
            case 'cancelled':
                return {
                    label: 'Richiesta Annullata',
                    icon: '✕',
                    bg: 'bg-gray-100 text-gray-600 border-gray-200'
                };
            default:
                return {
                    label: action,
                    icon: '📝',
                    bg: 'bg-slate-100 text-slate-700 border-slate-200'
                };
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3 sm:p-6 animate-fade-in backdrop-blur-sm">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
                {/* MODAL HEADER */}
                <div className="p-5 sm:p-6 border-b border-gray-100 flex justify-between items-start sm:items-center bg-slate-900 text-white">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-2xl">🛡️</span>
                            <h3 className="text-lg sm:text-xl font-black tracking-tight">
                                Registro Supervisione & Grant Accessi
                            </h3>
                            <span className="bg-amber-400 text-slate-950 text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider">
                                SuperAdmin
                            </span>
                        </div>
                        <p className="text-xs text-slate-300 mt-1 font-medium">
                            Tracciamento e audit log di tutte le richieste di consultazione incontri e autorizzazioni deontologiche.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white font-bold text-xl p-1 rounded-full hover:bg-slate-800 transition"
                    >
                        ✕
                    </button>
                </div>

                {/* METRICS BAR */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-4 sm:p-6 bg-slate-50 border-b border-slate-100">
                    <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
                        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">Grant Attivi</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-2xl font-black text-slate-800">{activeGrants.length}</span>
                            <span className="text-lg">🛡️</span>
                        </div>
                    </div>

                    <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
                        <span className="text-[10px] font-black text-amber-600 uppercase tracking-wider">In Attesa</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-2xl font-black text-slate-800">{pendingRequests.length}</span>
                            <span className="text-lg">⏳</span>
                        </div>
                    </div>

                    <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
                        <span className="text-[10px] font-black text-rose-600 uppercase tracking-wider">Respinte</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-2xl font-black text-slate-800">{rejectedRequests.length}</span>
                            <span className="text-lg">🚫</span>
                        </div>
                    </div>

                    <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs flex flex-col justify-between">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Eventi Totali</span>
                        <div className="flex items-center justify-between mt-1">
                            <span className="text-2xl font-black text-slate-800">{allLogs.length}</span>
                            <span className="text-lg">📜</span>
                        </div>
                    </div>
                </div>

                {/* FILTERS & SEARCH */}
                <div className="p-4 sm:px-6 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between bg-white">
                    <div className="flex gap-1.5 overflow-x-auto pb-1 sm:pb-0 no-scrollbar">
                        {[
                            { id: 'all', label: 'Tutti', count: allLogs.length },
                            { id: 'approved', label: '🛡️ Grant Concessi', count: activeGrants.length },
                            { id: 'pending', label: '⏳ In Attesa', count: pendingRequests.length },
                            { id: 'rejected', label: '🚫 Respinti', count: rejectedRequests.length },
                            { id: 'revoked', label: '🔒 Storico / Revocati' }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setFilterStatus(tab.id)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap border ${
                                    filterStatus === tab.id
                                        ? 'bg-[#1e3847] text-white border-[#1e3847] shadow-xs'
                                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                }`}
                            >
                                {tab.label} {tab.count !== undefined && `(${tab.count})`}
                            </button>
                        ))}
                    </div>

                    <div className="relative min-w-[220px]">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Cerca cliente, counselor o nota..."
                            className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:border-[#1e3847] font-medium"
                        />
                        <span className="absolute left-2.5 top-2 text-xs text-slate-400">🔍</span>
                    </div>
                </div>

                {/* LOGS LIST */}
                <div className="p-4 sm:p-6 overflow-y-auto space-y-3 flex-1 bg-slate-50/50">
                    {filteredLogs.length === 0 ? (
                        <div className="p-12 text-center bg-white rounded-3xl border border-dashed border-slate-200 text-slate-400">
                            <span className="text-4xl block mb-2">📜</span>
                            <p className="font-bold text-slate-700">Nessun evento di autorizzazione registrato</p>
                            <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                                Le richieste di accesso per supervisione e i grant approvati o revocati compariranno automaticamente in questo registro.
                            </p>
                        </div>
                    ) : (
                        filteredLogs.map(log => {
                            const badge = getActionBadge(log.action);
                            return (
                                <div
                                    key={log.id}
                                    className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200/80 shadow-xs hover:shadow-md transition flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                                >
                                    <div className="flex-1 space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-md border flex items-center gap-1 ${badge.bg}`}>
                                                <span>{badge.icon}</span>
                                                <span>{badge.label}</span>
                                            </span>
                                            <span className="text-[11px] font-mono text-slate-400">
                                                {formatLogDate(log.timestamp)}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <h4 className="text-sm font-bold text-slate-800">
                                                {log.clientName}
                                            </h4>
                                            <span className="text-[11px] text-slate-500 font-medium">
                                                ({log.clientEmail})
                                            </span>
                                        </div>

                                        <div className="text-xs text-slate-600 flex items-center gap-1.5 flex-wrap">
                                            <span className="text-slate-400 font-bold">Counselor Titolare:</span>
                                            <span className="font-bold text-indigo-900 bg-indigo-50 px-2 py-0.5 rounded-md">
                                                🧑‍💼 {log.counselorName}
                                            </span>
                                            {log.userName && (
                                                <span className="text-slate-400 text-[11px]">
                                                    • Operatore: <em>{log.userName}</em>
                                                </span>
                                            )}
                                        </div>

                                        {log.notes && (
                                            <p className="text-[11px] text-slate-600 bg-slate-50 p-2 rounded-xl border border-slate-100 italic">
                                                «{log.notes}»
                                            </p>
                                        )}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                                        <button
                                            onClick={() => {
                                                onClose();
                                                if (log.rawClient) {
                                                    onOpenClient(log.rawClient);
                                                } else {
                                                    const target = clients.find(c => c.id === log.clientId);
                                                    if (target) onOpenClient(target);
                                                }
                                            }}
                                            className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition shadow-xs flex items-center gap-1.5"
                                        >
                                            <span>📂</span> Apri Cartella
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* MODAL FOOTER */}
                <div className="p-4 border-t border-slate-100 bg-white flex justify-between items-center text-xs text-slate-500">
                    <span>
                        Codice Deontologico: Riservatezza e Segreto Professionale (Legge 4/2013)
                    </span>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition"
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SupervisionAccessLogModal;
